import { onDestroy } from "svelte";
import { exportCvrCompaniesExcel, getPagedCvrCompanies } from "$lib/services/cvr-company-service";
import type { CvrCompanyQuery } from "$lib/types/cvr-company-query";
import type { CvrCompanyRow } from "$lib/types/cvr-company-row";
import type { CvrFilters } from "$lib/types/cvr-filters";
import type { CvrSortBy } from "$lib/types/cvr-sort-by";
import type { CvrSortDirection } from "$lib/types/cvr-sort-direction";

export const useCvrTable = () => {
  const sortByName = 0;
  const sortDirectionAsc = 0;
  const sortDirectionDesc = 1;
  const sortByEmployees: CvrSortBy = 4;

  const pageSizeOptions = [15, 30, 50, 100];
  const sortByOptions = [
    { value: 0, label: "Name" },
    { value: 1, label: "Branche" },
    { value: 2, label: "Created" },
    { value: 3, label: "Protected" },
    { value: sortByEmployees, label: "Employees" }
  ] as const;

  let rows = $state<CvrCompanyRow[]>([]);
  let isLoading = $state(true);
  let errorMessage = $state("");
  let page = $state(1);
  let pageSize = $state(15);
  let totalCount = $state(0);
  let sortBy = $state<CvrSortBy>(sortByName);
  let sortDirection = $state<CvrSortDirection>(sortDirectionAsc);
  let searchInput = $state("");
  let searchTerm = $state("");
  let searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  let companyFormDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  const searchDebounceMs = 450;
  const companyFormDebounceMs = 500;
  let minEmployees = $state<number | null>(null);
  let maxEmployees = $state<number | null>(null);
  let createdFrom = $state<string | null>(null);
  let createdTo = $state<string | null>(null);
  let selectedCompanyForms = $state<string[]>([]);
  let reklamebeskyttetFilter = $state<boolean | null>(null);
  let hasWebsiteFilter = $state<boolean | null>(null);
  let isExportModalOpen = $state(false);
  let isExporting = $state(false);
  let exportErrorMessage = $state("");

  const availableCompanyForms = $derived.by(() => {
    const fromRows = rows
      .map((row) => row.virksomhedsform?.trim() ?? "")
      .filter((value) => value.length > 0);

    return [...new Set([...selectedCompanyForms, ...fromRows])].sort((a, b) => a.localeCompare(b));
  });

  const activeFilterBadges = $derived.by(() => {
    const badges: string[] = [];

    if (minEmployees !== null || maxEmployees !== null) {
      badges.push(`Employees: ${minEmployees ?? "-"} to ${maxEmployees ?? "-"}`);
    }

    if (createdFrom !== null || createdTo !== null) {
      badges.push(`Created: ${createdFrom ?? "-"} to ${createdTo ?? "-"}`);
    }

    if (selectedCompanyForms.length > 0) {
      badges.push(`Company forms: ${selectedCompanyForms.join(", ")}`);
    }

    if (reklamebeskyttetFilter !== null) {
      badges.push(`Protected: ${reklamebeskyttetFilter ? "Yes" : "No"}`);
    }

    if (hasWebsiteFilter !== null) {
      badges.push(`Has website: ${hasWebsiteFilter ? "Yes" : "No"}`);
    }

    return badges;
  });

  const totalPages = $derived(Math.max(1, Math.ceil(totalCount / pageSize)));
  const canGoPrevious = $derived(page > 1);
  const canGoNext = $derived(page < totalPages);
  const fromRow = $derived(totalCount === 0 ? 0 : (page - 1) * pageSize + 1);
  const toRow = $derived(Math.min(page * pageSize, totalCount));

  const loadCompanies = async () => {
    isLoading = true;
    errorMessage = "";
    try {
      const query: CvrCompanyQuery = {
        page,
        pageSize,
        sortBy,
        sortDirection,
        searchTerm: searchTerm.trim().length > 0 ? searchTerm.trim() : null,
        filters: {
          minEmployees,
          maxEmployees,
          createdFrom,
          createdTo,
          companyForms: selectedCompanyForms,
          reklamebeskyttet: reklamebeskyttetFilter,
          hasWebsite: hasWebsiteFilter
        } satisfies CvrFilters
      };

      const payload = await getPagedCvrCompanies(query);
      rows = payload.items;
      totalCount = payload.totalCount;
      page = payload.page;
      pageSize = payload.pageSize;
    } catch {
      errorMessage = "Failed to load CVR companies.";
      rows = [];
      totalCount = 0;
    } finally {
      isLoading = false;
    }
  };

  const changePageSize = async (nextPageSize: number) => {
    pageSize = nextPageSize;
    page = 1;
    await loadCompanies();
  };

  const goPrevious = async () => {
    if (!canGoPrevious) {
      return;
    }

    page -= 1;
    await loadCompanies();
  };

  const goNext = async () => {
    if (!canGoNext) {
      return;
    }

    page += 1;
    await loadCompanies();
  };

  const changeSortBy = async (nextSortBy: number) => {
    sortBy = nextSortBy as CvrSortBy;
    page = 1;
    await loadCompanies();
  };

  const toggleSortDirection = async () => {
    sortDirection = sortDirection === sortDirectionAsc
      ? sortDirectionDesc
      : sortDirectionAsc;
    page = 1;
    await loadCompanies();
  };

  const scheduleSearch = (value: string) => {
    searchInput = value;

    if (searchDebounceHandle !== null) {
      clearTimeout(searchDebounceHandle);
    }

    searchDebounceHandle = setTimeout(async () => {
      const nextSearchTerm = searchInput.trim();
      if (nextSearchTerm === searchTerm) {
        return;
      }

      searchTerm = nextSearchTerm;
      page = 1;
      await loadCompanies();
    }, searchDebounceMs);
  };

  const initialize = () => {
    void loadCompanies();
  };

  const setMinEmployees = async (value: number | null) => {
    minEmployees = value;
    page = 1;
    await loadCompanies();
  };

  const setMaxEmployees = async (value: number | null) => {
    maxEmployees = value;
    page = 1;
    await loadCompanies();
  };

  const toggleCompanyForm = async (value: string) => {
    const normalized = value.trim();
    if (normalized.length === 0) {
      return;
    }

    selectedCompanyForms = selectedCompanyForms.includes(normalized)
      ? selectedCompanyForms.filter((item) => item !== normalized)
      : [...selectedCompanyForms, normalized];

    page = 1;
    if (companyFormDebounceHandle !== null) {
      clearTimeout(companyFormDebounceHandle);
    }

    companyFormDebounceHandle = setTimeout(async () => {
      await loadCompanies();
    }, companyFormDebounceMs);
  };

  const setCreatedFrom = async (value: string | null) => {
    createdFrom = value;
    page = 1;
    await loadCompanies();
  };

  const setCreatedTo = async (value: string | null) => {
    createdTo = value;
    page = 1;
    await loadCompanies();
  };

  const setReklamebeskyttetFilter = async (value: boolean | null) => {
    reklamebeskyttetFilter = value;
    page = 1;
    await loadCompanies();
  };

  const setHasWebsiteFilter = async (value: boolean | null) => {
    hasWebsiteFilter = value;
    page = 1;
    await loadCompanies();
  };

  const clearFilterBadge = async (badge: string) => {
    if (badge.startsWith("Employees:")) {
      minEmployees = null;
      maxEmployees = null;
    } else if (badge.startsWith("Created:")) {
      createdFrom = null;
      createdTo = null;
    } else if (badge.startsWith("Company forms:")) {
      selectedCompanyForms = [];
    } else if (badge.startsWith("Protected:")) {
      reklamebeskyttetFilter = null;
    } else if (badge.startsWith("Has website:")) {
      hasWebsiteFilter = null;
    }

    page = 1;
    await loadCompanies();
  };

  const openExportModal = () => {
    exportErrorMessage = "";
    isExportModalOpen = true;
  };

  const closeExportModal = () => {
    if (isExporting) {
      return;
    }

    exportErrorMessage = "";
    isExportModalOpen = false;
  };

  const downloadExcel = async () => {
    isExporting = true;
    exportErrorMessage = "";

    try {
      await exportCvrCompaniesExcel(
        sortBy,
        sortDirection,
        searchTerm.trim().length > 0 ? searchTerm.trim() : null,
        {
          minEmployees,
          maxEmployees,
          createdFrom,
          createdTo,
          companyForms: selectedCompanyForms,
          reklamebeskyttet: reklamebeskyttetFilter,
          hasWebsite: hasWebsiteFilter
        }
      );

      isExportModalOpen = false;
    } catch {
      exportErrorMessage = "Failed to export Excel.";
    } finally {
      isExporting = false;
    }
  };

  onDestroy(() => {
    if (searchDebounceHandle !== null) {
      clearTimeout(searchDebounceHandle);
    }

    if (companyFormDebounceHandle !== null) {
      clearTimeout(companyFormDebounceHandle);
    }
  });

  return {
    pageSizeOptions,
    sortByOptions,
    get rows() {
      return rows;
    },
    get isLoading() {
      return isLoading;
    },
    get errorMessage() {
      return errorMessage;
    },
    get page() {
      return page;
    },
    get pageSize() {
      return pageSize;
    },
    get totalCount() {
      return totalCount;
    },
    get sortBy() {
      return sortBy;
    },
    get sortDirection() {
      return sortDirection;
    },
    get searchInput() {
      return searchInput;
    },
    get minEmployees() {
      return minEmployees;
    },
    get maxEmployees() {
      return maxEmployees;
    },
    get createdFrom() {
      return createdFrom;
    },
    get createdTo() {
      return createdTo;
    },
    get selectedCompanyForms() {
      return selectedCompanyForms;
    },
    get availableCompanyForms() {
      return availableCompanyForms;
    },
    get reklamebeskyttetFilter() {
      return reklamebeskyttetFilter;
    },
    get hasWebsiteFilter() {
      return hasWebsiteFilter;
    },
    get activeFilterBadges() {
      return activeFilterBadges;
    },
    get isExportModalOpen() {
      return isExportModalOpen;
    },
    get isExporting() {
      return isExporting;
    },
    get exportErrorMessage() {
      return exportErrorMessage;
    },
    get totalPages() {
      return totalPages;
    },
    get canGoPrevious() {
      return canGoPrevious;
    },
    get canGoNext() {
      return canGoNext;
    },
    get fromRow() {
      return fromRow;
    },
    get toRow() {
      return toRow;
    },
    initialize,
    changePageSize,
    goPrevious,
    goNext,
    changeSortBy,
    toggleSortDirection,
    scheduleSearch,
    setMinEmployees,
    setMaxEmployees,
    setCreatedFrom,
    setCreatedTo,
    toggleCompanyForm,
    setReklamebeskyttetFilter,
    setHasWebsiteFilter,
    clearFilterBadge,
    openExportModal,
    closeExportModal,
    downloadExcel
  };
};
